<template>
  <transition
    name="embed__transition"
    mode="out-in"
    appear
  >
    <div class="embed">
      <div class="embed__header">
        <h5>Embed Dataset</h5>

        <div class="embed__close">
          <UtilityIconButton
            size="md"
            variant="round"
            backgroundColor="#12130f"
            highlightColor="rgba(255,255,255,0.7)"
            highlightBackgroundColor="#070806"
            content="Close"
            v-tippy="{ theme: 'statzon', placement: 'left', arrow: true, animation: 'scale' }"
            @click.native="close()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M23 20.168l-8.185-8.187 8.185-8.174-2.832-2.807-8.182 8.179-8.176-8.179-2.81 2.81 8.186 8.196-8.186 8.184 2.81 2.81 8.203-8.192 8.18 8.192z"/></svg>
          </UtilityIconButton>
        </div>
      </div>
      <div v-if="!embedId">
        <div class="row row--border row--separator">
          <div class="row__full">
            <p>You need license for this dataset to get your embedding code.</p>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="row row--border row--separator">
          <ChartEmbedPreview
            v-if="type.key !== 'datatable'"
            :id="embedId"
            :key="embedId + type.key"
            :type="type.key"
            :data="results"
            :year="year.key"
            class="embed__chart"
          />
          <div
            v-else
            class="embed__preview"
          >
            <h2>No preview for datatable.</h2>
          </div>
        </div>
        <div class="row row--border row--separator">
          <div
            id="embed-code"
            class="embed__code"
            contenteditable
            autocorrect="off"
            spellcheck="false"
            @click="copyEmbedCode()"
          >
            <div class="embed__iframe">{{ iFrameHTML }}</div>
          </div>
        </div>
        <div class="row row--border row--separator">
          <div class="row__full">
            <div class="embed__typeSelector">
              <FormSelect
                v-model="type"
                :options="typeOptions"
                :icon="type.icon"
                dropdownHeight="136px"
                label="Default type"
                alt-label="Default type"
                @input="setIFrameHTML()"
              />
            </div>
            <div class="embed__yearSelector">
              <FormSelect
                v-model="year"
                :disabled="type.key !== 'pie'"
                :options="chartYears"
                :icon="year.icon"
                dropdownHeight="136px"
                label="Default year (Pie)"
                alt-label="Default year (Pie)" @input="setIFrameHTML()"
              />
            </div>
          </div>
        </div>
        <div class="row row--border row--separator">
          <div class="row__full">
            <div style="margin-left: auto;">
              <UtilityCtaButton
                icon='<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 8V5.2C8 4.0799 8 3.51984 8.21799 3.09202C8.40973 2.71569 8.71569 2.40973 9.09202 2.21799C9.51984 2 10.0799 2 11.2 2H18.8C19.9201 2 20.4802 2 20.908 2.21799C21.2843 2.40973 21.5903 2.71569 21.782 3.09202C22 3.51984 22 4.0799 22 5.2V12.8C22 13.9201 22 14.4802 21.782 14.908C21.5903 15.2843 21.2843 15.5903 20.908 15.782C20.4802 16 19.9201 16 18.8 16H16M5.2 22H12.8C13.9201 22 14.4802 22 14.908 21.782C15.2843 21.5903 15.5903 21.2843 15.782 20.908C16 20.4802 16 19.9201 16 18.8V11.2C16 10.0799 16 9.51984 15.782 9.09202C15.5903 8.71569 15.2843 8.40973 14.908 8.21799C14.4802 8 13.9201 8 12.8 8H5.2C4.0799 8 3.51984 8 3.09202 8.21799C2.71569 8.40973 2.40973 8.71569 2.21799 9.09202C2 9.51984 2 10.0799 2 11.2V18.8C2 19.9201 2 20.4802 2.21799 20.908C2.40973 21.2843 2.71569 21.5903 3.09202 21.782C3.51984 22 4.07989 22 5.2 22Z" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
                :round="true"
                color="bg-1"
                size="md"
                @click="copyEmbedCode()"
              >
                {{ copyStatus }}
              </UtilityCtaButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import ChartEmbedPreview from '@/components/chart/EmbedPreview.vue'
import UtilityCtaButton from '@/components/utility/CtaButton.vue'
import UtilityIconButton from '@/components/utility/IconButton.vue'
import FormSelect from '@/components/form/Select.vue'
import helperMethods from '@/helpers/utility'

export default {
  components: {
    ChartEmbedPreview,
    UtilityCtaButton,
    UtilityIconButton,
    FormSelect
  },
  data () {
    return {
      iFrameHTML: '',
      copyStatus: 'Copy',
      type: {
        'key': 'default',
        'name': 'Default',
        'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M4 17.162l-2 .838v-12.972l12-5.028v2.507l-10 4.19v10.465zm18-11.162l-12 5.028v12.972l12-5.028v-12.972zm-14 3.697l10-4.19v-2.507l-12 5.028v12.972l2-.838v-10.465z"/></svg>'
      },
      year: {
        'key': 'default',
        'name': 'Default',
        'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M13.012 5.007v-1.668l2.802-2.771c.409.136.809.293 1.197.471l-3.999 3.968zm7.089-1.93l-7.089 7.058v.853h.877l7.044-7.076c-.263-.292-.541-.57-.832-.835zm-7.089-1.468l1.437-1.406c-.46-.094-.96-.163-1.437-.203v1.609zm10.789 7.962l-1.386 1.417h1.585c-.04-.47-.106-.964-.199-1.417zm-.363-1.366c-.135-.41-.292-.81-.469-1.199l-3.951 3.982h1.668l2.752-2.783zm-1.063-2.337c-.205-.346-.426-.682-.664-1.004l-6.093 6.124h1.668l5.089-5.12zm-3.225-3.57c-.322-.238-.657-.459-1.003-.665l-5.135 5.104v1.668l6.138-6.107zm-8.15 10.702v-13c-6.161.519-11 5.683-11 11.978 0 6.639 5.382 12.022 12.021 12.022 6.296 0 11.46-4.839 11.979-11h-13z"/></svg>'
      },
      typeOptions: [
        {
          'key': 'default',
          'name': 'Default',
          'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M4 17.162l-2 .838v-12.972l12-5.028v2.507l-10 4.19v10.465zm18-11.162l-12 5.028v12.972l12-5.028v-12.972zm-14 3.697l10-4.19v-2.507l-12 5.028v12.972l2-.838v-10.465z"/></svg>'
        },
        {
          'key': 'ver_bar',
          'name': 'Vertical Bar',
          'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M5 19h-4v-8h4v8zm6 0h-4v-18h4v18zm6 0h-4v-12h4v12zm6 0h-4v-4h4v4zm1 2h-24v2h24v-2z"/></svg>'
        },
        {
          'key': 'hor_bar',
          'name': 'Horizontal Bar',
          'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M5 19h-4v-8h4v8zm6 0h-4v-18h4v18zm6 0h-4v-12h4v12zm6 0h-4v-4h4v4zm1 2h-24v2h24v-2z"/></svg>'
        },
        {
          'key': 'stacked_ver_bar',
          'name': 'Stacked Vertical Bar',
          'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M4 17.162l-2 .838v-12.972l12-5.028v2.507l-10 4.19v10.465zm18-11.162l-12 5.028v12.972l12-5.028v-12.972zm-14 3.697l10-4.19v-2.507l-12 5.028v12.972l2-.838v-10.465z"/></svg>'
        },
        {
          'key': 'stacked_hor_bar',
          'name': 'Stacked Horizontal Bar',
          'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M4 17.162l-2 .838v-12.972l12-5.028v2.507l-10 4.19v10.465zm18-11.162l-12 5.028v12.972l12-5.028v-12.972zm-14 3.697l10-4.19v-2.507l-12 5.028v12.972l2-.838v-10.465z"/></svg>'
        },
        {
          'key': 'hor_line',
          'name': 'Horizontal Line',
          'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill-rule="evenodd" clip-rule="evenodd"><path d="M18.799 7.038c-.496-.535-.799-1.252-.799-2.038 0-1.656 1.344-3 3-3s3 1.344 3 3-1.344 3-3 3c-.146 0-.29-.01-.431-.031l-3.333 6.032c.475.53.764 1.231.764 1.999 0 1.656-1.344 3-3 3s-3-1.344-3-3c0-.583.167-1.127.455-1.587l-2.565-3.547c-.281.087-.58.134-.89.134l-.368-.022-3.355 6.069c.451.525.723 1.208.723 1.953 0 1.656-1.344 3-3 3s-3-1.344-3-3 1.344-3 3-3c.186 0 .367.017.543.049l3.298-5.967c-.52-.539-.841-1.273-.841-2.082 0-1.656 1.344-3 3-3s3 1.344 3 3c0 .617-.187 1.191-.507 1.669l2.527 3.495c.307-.106.637-.164.98-.164.164 0 .325.013.482.039l3.317-6.001zm-3.799 7.962c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1zm-6-8c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1z"/></svg>'
        },
        {
          'key': 'ver_line',
          'name': 'Vertical Line',
          'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill-rule="evenodd" clip-rule="evenodd"><path d="M18.799 7.038c-.496-.535-.799-1.252-.799-2.038 0-1.656 1.344-3 3-3s3 1.344 3 3-1.344 3-3 3c-.146 0-.29-.01-.431-.031l-3.333 6.032c.475.53.764 1.231.764 1.999 0 1.656-1.344 3-3 3s-3-1.344-3-3c0-.583.167-1.127.455-1.587l-2.565-3.547c-.281.087-.58.134-.89.134l-.368-.022-3.355 6.069c.451.525.723 1.208.723 1.953 0 1.656-1.344 3-3 3s-3-1.344-3-3 1.344-3 3-3c.186 0 .367.017.543.049l3.298-5.967c-.52-.539-.841-1.273-.841-2.082 0-1.656 1.344-3 3-3s3 1.344 3 3c0 .617-.187 1.191-.507 1.669l2.527 3.495c.307-.106.637-.164.98-.164.164 0 .325.013.482.039l3.317-6.001zm-3.799 7.962c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1zm-6-8c.552 0 1 .448 1 1s-.448 1-1 1-1-.448-1-1 .448-1 1-1z"/></svg>'
        },
        {
          'key': 'pie',
          'name': 'Pie',
          'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M13.012 5.007v-1.668l2.802-2.771c.409.136.809.293 1.197.471l-3.999 3.968zm7.089-1.93l-7.089 7.058v.853h.877l7.044-7.076c-.263-.292-.541-.57-.832-.835zm-7.089-1.468l1.437-1.406c-.46-.094-.96-.163-1.437-.203v1.609zm10.789 7.962l-1.386 1.417h1.585c-.04-.47-.106-.964-.199-1.417zm-.363-1.366c-.135-.41-.292-.81-.469-1.199l-3.951 3.982h1.668l2.752-2.783zm-1.063-2.337c-.205-.346-.426-.682-.664-1.004l-6.093 6.124h1.668l5.089-5.12zm-3.225-3.57c-.322-.238-.657-.459-1.003-.665l-5.135 5.104v1.668l6.138-6.107zm-8.15 10.702v-13c-6.161.519-11 5.683-11 11.978 0 6.639 5.382 12.022 12.021 12.022 6.296 0 11.46-4.839 11.979-11h-13z"/></svg>'
        },
        {
          'key': 'datatable',
          'name': 'Datatable',
          'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M17 13h-10v-1h10v1zm0 2h-10v1h10v-1zm0 3h-10v1h10v-1zm5-16v22h-20v-22h3v1c0 1.103.897 2 2 2s2-.897 2-2v-1h6v1c0 1.103.897 2 2 2s2-.897 2-2v-1h3zm-2 7h-16v13h16v-13zm-12-8c0-.552-.447-1-1-1s-1 .448-1 1v2c0 .552.447 1 1 1s1-.448 1-1v-2zm10 0c0-.552-.447-1-1-1s-1 .448-1 1v2c0 .552.447 1 1 1s1-.448 1-1v-2z"/></svg>'
        }
      ]
    }
  },
  computed: {
    embedId () {
      return this.$store.getters['search/getAddingEmbed']
    },
    chartYears () {
      let yearList = this.$store.getters['search/getResults'].dataset.byId['DATASET_' + this.$store.getters['search/getSelectedId']].attributes.years
      let years = [{
        'key': 'default',
        'name': 'Default',
        'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M13.012 5.007v-1.668l2.802-2.771c.409.136.809.293 1.197.471l-3.999 3.968zm7.089-1.93l-7.089 7.058v.853h.877l7.044-7.076c-.263-.292-.541-.57-.832-.835zm-7.089-1.468l1.437-1.406c-.46-.094-.96-.163-1.437-.203v1.609zm10.789 7.962l-1.386 1.417h1.585c-.04-.47-.106-.964-.199-1.417zm-.363-1.366c-.135-.41-.292-.81-.469-1.199l-3.951 3.982h1.668l2.752-2.783zm-1.063-2.337c-.205-.346-.426-.682-.664-1.004l-6.093 6.124h1.668l5.089-5.12zm-3.225-3.57c-.322-.238-.657-.459-1.003-.665l-5.135 5.104v1.668l6.138-6.107zm-8.15 10.702v-13c-6.161.519-11 5.683-11 11.978 0 6.639 5.382 12.022 12.021 12.022 6.296 0 11.46-4.839 11.979-11h-13z"/></svg>'
      }]
      yearList.forEach(year => {
        years.push({
          'key': year,
          'name': year,
          'icon': '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M13.012 5.007v-1.668l2.802-2.771c.409.136.809.293 1.197.471l-3.999 3.968zm7.089-1.93l-7.089 7.058v.853h.877l7.044-7.076c-.263-.292-.541-.57-.832-.835zm-7.089-1.468l1.437-1.406c-.46-.094-.96-.163-1.437-.203v1.609zm10.789 7.962l-1.386 1.417h1.585c-.04-.47-.106-.964-.199-1.417zm-.363-1.366c-.135-.41-.292-.81-.469-1.199l-3.951 3.982h1.668l2.752-2.783zm-1.063-2.337c-.205-.346-.426-.682-.664-1.004l-6.093 6.124h1.668l5.089-5.12zm-3.225-3.57c-.322-.238-.657-.459-1.003-.665l-5.135 5.104v1.668l6.138-6.107zm-8.15 10.702v-13c-6.161.519-11 5.683-11 11.978 0 6.639 5.382 12.022 12.021 12.022 6.296 0 11.46-4.839 11.979-11h-13z"/></svg>'
        })
      })
      return years
    },
    results () {
      return this.$store.getters['search/getResults'].dataset.byId['DATASET_' + this.$store.getters['search/getSelectedId']].attributes.data_series
    }
  },
  mounted () {
    this.setIFrameHTML()
  },
  methods: {
    setIFrameHTML () {
      this.copyStatus = 'Copy'
      let parameters = []
      let parameterStr = ''

      if (this.type.key !== 'default') {
        parameters.push('type=' + this.type.key)
      }

      if (this.type.key === 'pie' && this.year.key !== 'default') {
        parameters.push('year=' + this.year.key)
      }

      if (parameters.length > 0) {
        parameterStr = '?' + parameters.join('&')
      }
      
      let html = '<iframe height="100%" width="100%" src="' + process.env.VUE_APP_URL + (process.env.VUE_APP_URL.includes('statzon.local') ? '#/' : '') + 'embeds/' + this.embedId + parameterStr + '" frameborder="0"></iframe>'

      this.iFrameHTML = html
    },
    copyEmbedCode () {
      let textElem = document.getElementById('embed-code')

      if (document.selection) {
        var range = document.body.createTextRange();
        range.moveToElementText(textElem);
        range.select();
      } else if (window.getSelection) {
          var range = document.createRange();
          range.selectNode(textElem);
          window.getSelection().removeAllRanges();
          window.getSelection().addRange(range);
      }
      // execCommand() is officially obsolete/deprecated but there's no alternative.
      document.execCommand('copy')
      this.copyStatus = 'Copied!'
      let that = this
      setTimeout(function () {
        that.copyStatus = 'Copy'
      }, 2500)
    },
    close () {
      this.$emit('closed')
    },
    ...helperMethods
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.embed {
  @include font('content--md', $white, 0.9);
  position: absolute;
  top: 0px;
  width: 100%;
  height: auto;
  text-align: center;
  height: auto;
  background-color: $bg-3;
  border: 1px solid $black;

  @media screen and (min-width: $breakpointSm) {
    width: 448px;
  }

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg', $white, 0.9);
  }

  &__transition {
    &-enter-active, &-leave-active {
      transition: opacity 0.25s ease-out, transform 0.25s ease-out;
      transform: scale(1);
    }

    &-enter, &-leave-to {
      opacity: 0;
      transform: translateY(-20%) scale(0.6);
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    background-color: $bg-2;
    padding: 8px 12px 8px 16px;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;

    h5 {
      @include font('header--sm');
      text-transform: uppercase;
    }
  }

  &__top {
    display: flex;
    justify-content: space-between;
    padding: 0.4rem 1rem;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;

    &--light {
      color: rgba($white, 0.7);
    }

    &--danger {
      color: $danger;
    }

    &--warning {
      color: $warning;
    }
  }

  &__info {
    margin: 0rem auto;
    padding: 0.75rem 1.5rem 1.25rem 1.5rem;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    color: rgba($white, 0.9);

    p {
      margin: 0;
      padding: 0;
    }
  }

  &__form {
    border-top: 1px solid rgba($white, 0.1);
  }

  &__tooltip {
    svg {
      position: relative;
      top: 3px;
      left: 3px;
      width: 18px;
      height: 18px;
      fill: rgba($white, 0.7);

      @media screen and (min-width: $breakpointLg) {
        width: 20px;
        height: 20px;
        top: 4px;
      }

      &:hover {
        fill: rgba($blue, 1);
      }
    }
  }

  &__code {
    width: 100%;
    text-align: left;
    background-color: #12130f;
    padding: 0!important;
    border-radius: 4px;

    &:hover, &:active {
      background-color: #070806;
    } 
  }

  &__chart {
    height: 194px!important;
    width: 100%;
    border-radius: 4px;
    overflow: hidden;
  }

  &__preview {
    height: 194px;
    width: 100%;
    text-align: center;
    background-color: rgb(27,30,33);
    border-radius: 4px;
    overflow: hidden;

    h2 {
      margin: 0;
      padding: 16px;
    }
  }

  &__iframe {
    width: calc(100% - 1rem);
    padding: 0.5rem;
  }

  &__typeSelector {
    width: 67%;
    display: inline-block;
    padding-right: 1rem;
  }

  &__yearSelector {
    width: 33%;
    display: inline-block;
  }
}

.row {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin: 0.75rem auto;
  padding: 0rem 1rem;

  @media screen and (min-width: $breakpointLg) {
    margin: 1rem auto;
  }

  &--border {
    border-bottom: 1px solid $black;
    margin: 0.75rem auto 0 auto !important;
    padding-bottom: 1rem !important;
  }

  &--separator {
    border-top: 1px solid rgba($white, 0.1);
    padding: calc(1rem - 1px) 1rem 0 1rem;
    margin: 0 !important;

    &Top {
      margin-top: 0.5rem;
    }
  }

  &__full {
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;

    &--filters {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
    }

    &--condensed {
      justify-content: space-around;
    }
  }

  &__left {
    flex: 0.2;
    text-align: left;
  }

  &__right {
    flex: 0.8;
    text-align: left;
  }

  &__notice {
    color: rgba($white, 0.5);
    margin: 0 auto 1rem auto;
    display: block;
    font-style: italic;
  }

  &__center {
    margin: 0 auto;
    display: block;
  }

  &__error {
    color: $red;
    margin: 0;
  }

  &__label {
    color: rgba($white, 0.9);
    text-align: left;
  }

  &__operator {
    @include font('content--xs', $white, 0.5);
    position: relative;
    z-index: 9;
    font-weight: 500;
    margin: 0px 3px;
    text-transform: uppercase;
    display: inline-flex;

    /deep/ span {
      color: rgba($white, 0.7) !important;
    }

    &--active {
      color: rgba($white, 0.7);
    }
  }

  &__filter {
    position: relative;
    z-index: 10;
  }
}
</style>
